import { lazy, Suspense, useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { motion } from 'framer-motion';
import {
  LayoutDashboard,
  BarChart3,
  Users,
  GitBranch,
  Phone,
  FileText,
  Target,
  HeartPulse,
  LogOut,
  Plus,
  Settings,
  ChevronDown,
  HelpCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { cognitoLogout } from '../auth/config';
import { QuickLogProvider, useQuickLog } from '../components/QuickLogProvider';
import logoUrl from '../assets/logo.png';

// Pulls in the markdown renderer (~200 kB); load it only when opened.
const FieldGuideModal = lazy(() => import('../components/FieldGuideModal'));

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/weekly', label: 'Weekly', icon: BarChart3 },
  { to: '/legacy-chats', label: 'Legacy Spark Chats', icon: GitBranch },
  { to: '/discovery-calls', label: 'Discovery Calls', icon: Phone },
  { to: '/contacts', label: 'Contacts', icon: Users },
  { to: '/message-templates', label: 'Message Templates', icon: FileText },
];

const SETTINGS_ITEMS: NavItem[] = [
  { to: '/targets', label: 'Targets', icon: Target },
  { to: '/health', label: 'Health', icon: HeartPulse },
];

// Routes where the floating Quick Log button should NOT appear.
// These pages are config / reference / diagnostic — quick-logging from
// them would feel out of context.
const QUICK_LOG_HIDDEN_ROUTES: ReadonlySet<string> = new Set([
  '/message-templates',
  '/targets',
  '/health',
]);

export default function AppShell() {
  const auth = useAuth();
  const [guideOpen, setGuideOpen] = useState(false);
  const username =
    (auth.user?.profile.email as string | undefined) ?? auth.user?.profile.sub;

  async function handleSignOut() {
    await auth.removeUser();
    cognitoLogout();
  }

  return (
    <QuickLogProvider>
      <div className="flex h-screen flex-col overflow-hidden bg-brand-paper">
        <header className="flex items-center justify-between bg-brand-forest px-6 py-3 text-brand-paper shadow-sm">
          <div className="flex items-center gap-3">
            <img
              src={logoUrl}
              alt="360 Balanced Living"
              className="h-9 w-9 object-contain"
            />
            <span className="font-display text-lg font-black tracking-tight">
              Legacy Tracker
            </span>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <span className="hidden text-brand-sky sm:inline">{username}</span>
            <button
              type="button"
              onClick={handleSignOut}
              className="flex items-center gap-1.5 rounded-md border border-brand-sage/40 px-3 py-1.5 text-brand-paper transition hover:border-brand-lime hover:text-brand-lime"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </header>

        <div className="flex flex-1 min-h-0">
          <aside className="flex w-56 shrink-0 flex-col border-r border-brand-sage/30 bg-white py-4">
            <nav className="flex flex-col gap-1 px-3">
              {NAV_ITEMS.map((item) => (
                <NavItemLink key={item.to} item={item} />
              ))}
              <SettingsGroup />
            </nav>
            <div className="mt-auto flex flex-col gap-1 px-3 pt-3">
              <button
                type="button"
                onClick={() => setGuideOpen(true)}
                className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-brand-forest/80 transition hover:bg-brand-mint/15 hover:text-brand-forest"
              >
                <HelpCircle className="h-4 w-4" />
                Field guide
              </button>
              <div className="px-1 text-[11px] font-medium tabular-nums text-brand-sage/70">
                v{__APP_VERSION__}
              </div>
            </div>
          </aside>

          <main className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-6 lg:p-8">
            <Outlet />
          </main>
        </div>

        <FloatingQuickLog />
        {guideOpen && (
          <Suspense fallback={null}>
            <FieldGuideModal onClose={() => setGuideOpen(false)} />
          </Suspense>
        )}
      </div>
    </QuickLogProvider>
  );
}

function NavItemLink({ item, indented = false }: { item: NavItem; indented?: boolean }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        [
          'flex items-center gap-3 rounded-md py-2 text-sm transition',
          indented ? 'pl-9 pr-3' : 'px-3',
          isActive
            ? 'bg-brand-lime/15 font-bold text-brand-forest'
            : 'text-brand-forest/80 hover:bg-brand-mint/15 hover:text-brand-forest',
        ].join(' ')
      }
    >
      <item.icon className="h-4 w-4" />
      {item.label}
    </NavLink>
  );
}

function SettingsGroup() {
  const location = useLocation();
  const childActive = SETTINGS_ITEMS.some((item) => location.pathname === item.to);
  // Auto-open when navigating into a child route, but don't force-collapse
  // when leaving — user-controlled toggling wins after that.
  const [open, setOpen] = useState(childActive);
  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={[
          'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition',
          childActive
            ? 'bg-brand-lime/15 font-bold text-brand-forest'
            : 'text-brand-forest/80 hover:bg-brand-mint/15 hover:text-brand-forest',
        ].join(' ')}
      >
        <Settings className="h-4 w-4" />
        <span className="flex-1 text-left">Settings</span>
        <ChevronDown
          className={[
            'h-4 w-4 transition-transform',
            open ? 'rotate-0' : '-rotate-90',
          ].join(' ')}
        />
      </button>
      {open && (
        <div className="mt-1 flex flex-col gap-1">
          {SETTINGS_ITEMS.map((item) => (
            <NavItemLink key={item.to} item={item} indented />
          ))}
        </div>
      )}
    </div>
  );
}

function FloatingQuickLog() {
  const location = useLocation();
  const { open } = useQuickLog();
  if (QUICK_LOG_HIDDEN_ROUTES.has(location.pathname)) {
    return null;
  }
  return (
    <motion.button
      type="button"
      onClick={() => open(undefined)}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className="fixed bottom-8 right-8 z-40 flex items-center gap-2 rounded-full bg-brand-lime px-5 py-4 font-display font-black text-brand-forest shadow-xl ring-4 ring-brand-lime/30 transition hover:bg-brand-lime-600"
    >
      <Plus className="h-5 w-5" strokeWidth={3} />
      Quick log
    </motion.button>
  );
}